import { OP_AND, OP_EQUALS, OP_NOT_EQUALS } from "../actions/constants";
import { convertTokensToExpressions, treeToTokens } from "./treeUtils";

const evalLeaf = (node, given) => {
  const qId = node.vars[0].value;
  const opt = node.vars[1].value;
  const answers = given[qId] || [];

  const isEqual = answers.includes(opt);
  const ok = node.op === "e" ? isEqual : !isEqual;

  return { pass: ok, matched: ok ? 1 : 0, total: 1 };
};

const evalCondition = (node, given) => {
  // Leaf: e / ne
  if (node.op === OP_EQUALS || node.op === OP_NOT_EQUALS) return evalLeaf(node, given);

  const children = (node.vars || []).map((c) => evalCondition(c, given));

  // And
  if (node.op === OP_AND) {
    const pass = children.every((c) => c.pass);
    const matched = children.reduce((s, c) => s + c.matched, 0);
    const total = children.reduce((s, c) => s + c.total, 0);
    return { pass, matched, total };
  }

  // Or
  const passing = children.filter((c) => c.pass);
  if (passing.length === 0) {
    return {
      pass: false,
      matched: 0,
      total: children.reduce((m, c) => Math.max(m, c.total), 0),
    };
  }

  let best = passing[0];
  for (let i = 1; i < passing.length; i++) {
    if (passing[i].matched > best.matched) best = passing[i];
  }

  return { pass: true, matched: best.matched, total: best.total };
};

export const getBestMatchingQuota = (givenAnswers, quotas) => {
  const list = (quotas && quotas.quota) || [];
  const matchedQuotas = [];

  for (const q of list) {
    const res = evalCondition(q.condition, givenAnswers);

    if (!res.pass) continue;

    matchedQuotas.push({
      groupId: q.details.groupId,
      quotaName: q.details.quotaName,
      matched: res.matched,
    });
  }

  // Sort from highest match -> lowest match
  matchedQuotas.sort((a, b) => b.matched - a.matched);

  return matchedQuotas;
};

export const verifyQuotaIntegrity = (questions = [], quotas = []) => {
  const quotaValidationMap = {};

  quotas?.forEach((quota) => {
    let errors = {};

    // If Statement (checking Condition block)
    const expressions = convertTokensToExpressions(treeToTokens(quota?.condition))?.expression;
    expressions?.forEach((expression, expressionIndex) => {
      const question = questions?.find((question) => question?.id === expression?.question);
      if (!question) {
        // question should be in questions
        if (!errors["condition"]) errors["condition"] = {};
        if (!errors["condition"][expressionIndex]) errors["condition"][expressionIndex] = [];
        errors["condition"][expressionIndex].push(
          `Condition in question ${quota?.details?.groupId} referes to an unobserved question ${expression?.question}`
        );
      }

      const option = question?.options?.find((option) => {
        return option?.title === expression?.option;
      });
      if (!option) {
        // option should be an valid option of question
        if (!errors["condition"]) errors["condition"] = {};
        if (!errors["condition"][expressionIndex]) errors["condition"][expressionIndex] = [];
        errors["condition"][expressionIndex].push(
          `Condition in question ${quota?.details?.groupId} referes to an invalid option ${expression?.option} of question ${expression?.question}`
        );
      }
    });

    quotaValidationMap[quota?.details?.groupId] = {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  });

  return quotaValidationMap;
};

export const removeBrokenQuotas = (quotaValidationMap = {}, quotas = []) => {
  let updatedQuotas = JSON.parse(JSON.stringify(quotas));
  const brokenQuotas = Object.entries(quotaValidationMap)?.filter((item) => item[1]?.isValid === false);
  brokenQuotas?.forEach((item) => {
    const groupId = item[0];
    const details = item[1];
    const errors = details?.errors;

    if (errors) {
      const quotaErrorsArr = Object.entries(errors);
      for (let i = quotaErrorsArr.length - 1; i >= 0; i--) {
        const problematicQuota = updatedQuotas?.findIndex((i) => String(i.details?.groupId) === String(groupId));
        updatedQuotas?.splice(problematicQuota, 1);
      }
    }
  });
  return updatedQuotas;
};
